import type { EmissionFactor } from "../schema/master";

/**
 * 기본 배출계수.
 *
 * ⚠️ 전부 status=placeholder(임시값, 전문가 검토 전) 로 시작한다. 착수보고,KTR 1차 자문에서 값,출처,기준연도를
 *    확정하고 관리자 화면에서 교체한다. 인증제 모의산정용 계수는 인증운영기관 자료를 우선 검토한다.
 */
export const EMISSION_FACTORS: EmissionFactor[] = [
  {
    id: "ef.electricity.grid_kr",
    name: "국가 전력배출계수(계통전력)",
    kind: "electricity",
    value: 0.4594,
    unit: "kgCO2eq/kWh",
    source: "환경부 온실가스종합정보센터, 국가 전력배출계수(2019~2021년 3개년 평균, 2022년 공표)",
    sourceUrl: "https://www.gir.go.kr",
    baseYear: 2021,
    scope: "both",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: true,
    note: "2023년도 전력배출계수가 2025-12 공표되어 갱신주기가 1년으로 바뀌었다. 최신 공표값으로 교체 예정. 인증제 모의산정은 인증운영기관이 정한 계수 확인 필요.",
    active: true,
  },
  {
    id: "ef.electricity.renewable_simplified",
    name: "재생에너지 전력(단순화)",
    kind: "electricity",
    value: 0,
    unit: "kgCO2eq/kWh",
    source: "단순화 , 청정수소 인증제의 재생에너지 인정 전력을 배출 0 으로 봄",
    baseYear: 2024,
    scope: "both",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: false,
    note: "실제 LCA 에서는 발전설비 제조,설치,폐기 배출이 있다(태양광 약 0.04, 풍력 약 0.01 kgCO2eq/kWh).",
    active: true,
  },
  {
    id: "ef.electricity.solar_lca",
    name: "태양광 전과정 배출계수",
    kind: "electricity",
    value: 0.041,
    unit: "kgCO2eq/kWh",
    source: "IPCC AR5 WGIII Annex III, 유틸리티 규모 태양광 중앙값 41 gCO2eq/kWh",
    baseYear: 2014,
    scope: "general",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: false,
    active: true,
  },
  {
    id: "ef.electricity.wind_lca",
    name: "풍력 전과정 배출계수",
    kind: "electricity",
    value: 0.011,
    unit: "kgCO2eq/kWh",
    source: "IPCC AR5 WGIII Annex III, 육상풍력 중앙값 11 gCO2eq/kWh",
    baseYear: 2014,
    scope: "general",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: false,
    active: true,
  },
  {
    id: "ef.natural_gas.combustion",
    name: "천연가스 연소(원료,연료)",
    kind: "natural_gas",
    value: 2.69,
    unit: "kgCO2eq/kg",
    source: "2006 IPCC 가이드라인 Vol.2 , 천연가스 56.1 tCO2/TJ × 순발열량 48.0 TJ/Gg (CH4,N2O 미반영)",
    baseYear: 2006,
    scope: "both",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: true,
    note: "개질 공정에서는 원료로 들어간 탄소도 결국 CO2 로 나오므로 원료,연료 합산 사용량에 적용한다. 국가 고유 배출계수(LNG) 확인 필요.",
    active: true,
  },
  {
    id: "ef.natural_gas.upstream",
    name: "천연가스 공급망(채취,처리,수송) 상류 배출",
    kind: "natural_gas_upstream",
    value: 0.45,
    unit: "kgCO2eq/kg",
    source: "임시값 , 문헌 범위 0.3~0.7 kgCO2eq/kg NG (LNG 수입 경로 포함 시 상단)",
    baseYear: 2020,
    scope: "both",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: true,
    note: "인증제 Well-to-Gate 에서 원료 조달 배출에 해당. 인증운영기관 제공값 확인 필요.",
    active: true,
  },
  {
    id: "ef.diesel.combustion",
    name: "경유 연소(차량)",
    kind: "diesel",
    value: 2.65,
    unit: "kgCO2eq/L",
    source: "2006 IPCC 가이드라인 , 경유 74.1 tCO2/TJ × 순발열량 43.0 TJ/Gg × 밀도 0.832 kg/L",
    baseYear: 2006,
    scope: "both",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: true,
    note: "국가 고유 배출계수(경유, 약 2.58 kgCO2/L) 로 교체 검토.",
    active: true,
  },
  {
    id: "ef.steam.ng_boiler",
    name: "공정열,스팀(천연가스 보일러, 효율 85%)",
    kind: "steam",
    value: 0.066,
    unit: "kgCO2eq/MJ",
    source: "임시값 , 56.1 kgCO2/GJ ÷ 0.85",
    baseYear: 2006,
    scope: "both",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: true,
    active: true,
  },
  {
    id: "ef.hydrogen_vent",
    name: "수소 배출(손실) , 온실가스 미산정",
    kind: "hydrogen_vent",
    value: 0,
    unit: "kgCO2eq/kg",
    source: "IPCC AR5 에는 수소의 GWP 가 없다. ISO 19870,청정수소 인증제 현행 기준도 수소 누출을 온실가스로 산정하지 않는다.",
    baseYear: 2014,
    scope: "both",
    gwpBasis: "IPCC AR5 100년",
    status: "placeholder",
    isDefault: true,
    note: "AR6 이후 수소의 간접 GWP(약 11)가 논의되고 있다. 참고 안내.",
    active: true,
  },
];
